import _ from "lodash";
import { MICROSOFT_CLIENT_ID, MICROSOFT_SCOPES } from "Constants";
import {
  BrowserCacheLocation,
  PublicClientApplication,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { Sentry } from "services/sentry";

const request = { scopes: MICROSOFT_SCOPES };
class Microsoft {
  constructor() {
    if (!Microsoft.instance) {
      const msalConfig = {
        auth: {
          clientId: MICROSOFT_CLIENT_ID,
          redirectUri: window.location.origin + "/blank.html",
        },
        cache: {
          cacheLocation: BrowserCacheLocation.SessionStorage, // This configures where your cache will be stored
          storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
        },
      };
      this.userAgentApplication = new PublicClientApplication(msalConfig);
      Microsoft.instance = this;
    }
    return Microsoft.instance;
  }

  acquireTokenPopup = async ({ successCallback, failureCallback }) => {
    try {
      const response = await this.userAgentApplication.loginPopup({
        scopes: MICROSOFT_SCOPES,
        prompt: "select_account",
      });

      if (response) {
        this.userAgentApplication.setActiveAccount(response.account);
        successCallback(response.accessToken);
        return;
      }
    } catch (error) {
      failureCallback(error);
      console.error("ERROR_DURING_MIRCROSOFT_LOGIN_POPUP", error);
      if (
        !_.includes(
          ["user_cancelled", "interaction_in_progress"],
          _.get(error, "errorCode")
        )
      ) {
        Sentry.captureMessage("MICROSOFT_LOGIN_ERROR", {
          extra: {
            errorMessage: _.get(error, "message"),
            errorName: _.get(error, "name"),
          },
        });
      }
    }
  };

  getUserProfile = async ({
    successCallback,
    failureCallback,
    disablePopup = false,
  }) => {
    await this.userAgentApplication.initialize();

    const activeAccount = this.userAgentApplication.getActiveAccount();

    if (!activeAccount) {
      if (disablePopup) {
        return failureCallback("User not logged in");
      }

      return this.acquireTokenPopup({ successCallback, failureCallback });
    }

    try {
      const silentAccessToken = await this.userAgentApplication.acquireTokenSilent(
        request
      );
      return successCallback(silentAccessToken.accessToken);
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        return this.acquireTokenPopup({ successCallback, failureCallback });
      }
      failureCallback(error);
      console.error("ERROR_DURING_MIRCROSOFT_LOGIN_POPUP", error);
      Sentry.captureMessage("MICROSOFT_LOGIN_ERROR", {
        extra: {
          errorMessage: _.get(error, "message"),
          errorName: _.get(error, "name"),
        },
      });
    }
  };

  /**
   *
   * @public
   * @param {function} successCallback - Callback function to invoke upon successful token retrieval.
   * @param {function} failureCallback - Callback function to invoke upon token retrieval failure
   * @returns {void}
   */
  loginPopup = async ({ successCallback, failureCallback }) => {
    await this.userAgentApplication.initialize();

    return await this.acquireTokenPopup({ successCallback, failureCallback });
  };

  /**
   *
   * This function is invoked on every logout action;
   * Clears the active account to allow switching or logging in with another account.
   */
  logout = async () => {
    await this.userAgentApplication.clearCache();
    this.userAgentApplication.setActiveAccount(null);
  };
}

const instance = new Microsoft();
Object.freeze(instance);
export default instance;
